import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { GET_CA_OFFERS } from '../schemas/CaOffers';
import { IS_CA_MARKET } from '../constants/environment';
import {
  AudiCrediType,
  FinanceMinimumPaymentType,
  FinancePercentageTypes,
  LeaseMinimumPaymentType,
  LeasePercentageType,
  OfferPlusType,
  PromotionVehicleType,
} from '../types/offers';
import replaceModelFamily, { adjustBodyType, adjustModelName } from '../utils/replaceModelFamily';
import combineCpoOffers from '../utils/combineCpoOffers';
import { dateFormatter } from '../utils/formatDate';
import { selectZipCode } from '../reducers/zipCodeReducer';

interface OffersCATypes {
  getPromotionOfferByMultiParams: PromotionVehicleType[];
}

const useFetchCanOffers = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const { market } = useSelector(selectZipCode);

  const isCaMarket = market === 'CA';

  const { data, loading } = useQuery<OffersCATypes>(GET_CA_OFFERS, {
    errorPolicy: 'all',
    skip: !isCaMarket,
    variables: {
      year: '',
      type: '',
    },
  });
  const rawCaOffers = data?.getPromotionOfferByMultiParams;

  const newOffers = rawCaOffers
    ?.filter((toCheck) => toCheck.type === 'NEW-CAR')
    .flatMap((vehicle) => {
      const featuredOfferImageUrl = vehicle.legalBannerCn ?? vehicle.image; // Yes, we have featured image here for now. Hope temp.
      const offerDisclaimer = vehicle.legalCopyEn ?? '';
      const offerDisclaimerFr = vehicle.legalCopyFr ?? '';
      const offerImageUrl = vehicle.image;
      const offerModelDetail = {
        bodyType: adjustBodyType(vehicle.bodyStyleEn),
        drivetrain: null,
        fuelType: null,
        modelCode: vehicle.modelSalesCode,
        modelCodeDescription: vehicle.mtlTrimName ?? '',
        modelFamily: replaceModelFamily(vehicle.modelFamily, vehicle.modelName),
        modelYear: vehicle.year,
        modelName: adjustModelName(vehicle.modelName, vehicle.year, lang),
        msrp: vehicle.msrp ?? 0,
        productModelCode: vehicle.modelSalesCode,
        trimline: vehicle.mtlTrimName,
        programShortDescription: offerDisclaimer,
        dag: vehicle.dag,
        vehicleType: vehicle.type,
        buildAudiUrlEn: vehicle.configuratorUrlEn,
        buildAudiUrlFr: vehicle.configuratorUrlFr,
        modelId: vehicle.modelId,
      };

      const baseVehicleInfo = {
        offerDisclaimer,
        featuredOfferImageUrl,
        offerDisclaimerFr,
        offerImageUrl,
        offerModelDetail,
        stackableOffers: [],
        zipClusterID: 'null',
        programName: 'null',
        offerType: 'null',
        programPriority: vehicle.featuredOffer ? 1 : 2,
        programToDate: dateFormatter(vehicle.expiryDate, lang),
        programFromDate: 'null',
        programShortDescription: 'short description here',
        region: 'null',
        calendarMonth: 'null',
      };
      return vehicle?.offers?.map((offer) => {
        if (offer.title === 'Finance') {
          const aprRate =
            (
              offer.offertypes.find(
                (offerToCheck) => offerToCheck?.name === 'FinancePercentage',
              ) as FinancePercentageTypes
            )?.financeRate ?? 0;
          const term =
            (
              offer.offertypes.find(
                (offerToCheck) => offerToCheck?.name === 'FinancePercentage',
              ) as FinancePercentageTypes
            )?.financePeriod ?? 0;
          const creditBonus =
            (
              offer.offertypes.find(
                (offerToCheck) => offerToCheck?.name === 'FinanceCash',
              ) as AudiCrediType
            )?.audiCredit ?? 0;
          const customerDownPayment =
            (
              offer.offertypes.find(
                (offerToCheck) => offerToCheck?.name === 'FinanceMinimumPayment',
              ) as FinanceMinimumPaymentType
            )?.financeDownPayment ?? 0;
          const monthlyPayment =
            (
              offer.offertypes.find(
                (offerToCheck) => offerToCheck?.name === 'FinanceMinimumPayment',
              ) as FinanceMinimumPaymentType
            )?.financeMinimumPayment ?? 0;
          const paymentFreq =
            (
              offer.offertypes.find(
                (offerToCheck) => offerToCheck?.name === 'FinanceMinimumPayment',
              ) as FinanceMinimumPaymentType
            )?.financePaymentFrequency ?? null;
          const offerPlusEn =
            (
              offer.offertypes.find(
                (offerToCheck) => offerToCheck?.name === 'FinanceOffersPlus',
              ) as OfferPlusType
            )?.offerPlusEn ?? 0;
          const offerPlusFr =
            (
              offer.offertypes.find(
                (offerToCheck) => offerToCheck?.name === 'FinanceOffersPlus',
              ) as OfferPlusType
            )?.offerPlusFr ?? 0;
          return {
            ...baseVehicleInfo,
            aprRate,
            creditBonus,
            customerDownPayment,
            monthlyPayment,
            offerType: 'finance',
            programName: 'AUD_APR_SUBVENTION_SPEND',
            term,
            paymentFreq,
            totalDueAtSign: customerDownPayment,
            offerPlusEn,
            offerPlusFr,
          };
        }
        if (offer.title === 'Lease') {
          const aprRate =
            (
              offer.offertypes.find(
                (offerToCheck) => offerToCheck?.name === 'LeasePercentage',
              ) as LeasePercentageType
            )?.leaseRate ?? 0;
          const term =
            (
              offer.offertypes.find(
                (offerToCheck) => offerToCheck?.name === 'LeasePercentage',
              ) as LeasePercentageType
            )?.leasePeriod ?? 0;
          const creditBonus =
            (
              offer.offertypes.find(
                (offerToCheck) => offerToCheck?.name === 'LeaseCash',
              ) as AudiCrediType
            )?.audiCredit ?? 0;
          const customerDownPayment =
            (
              offer.offertypes.find(
                (offerToCheck) => offerToCheck?.name === 'LeaseMinimumPayment',
              ) as LeaseMinimumPaymentType
            )?.leaseDownPayment ?? 0;
          const monthlyPayment =
            (
              offer.offertypes.find(
                (offerToCheck) => offerToCheck?.name === 'LeaseMinimumPayment',
              ) as LeaseMinimumPaymentType
            )?.leaseMinimumPayment ?? 0;
          const paymentFreq =
            (
              offer.offertypes.find(
                (offerToCheck) => offerToCheck?.name === 'LeaseMinimumPayment',
              ) as LeaseMinimumPaymentType
            )?.leasePaymentFrequency ?? null;
          const offerPlusEn =
            (
              offer.offertypes.find(
                (offerToCheck) => offerToCheck?.name === 'LeaseOffersPlus',
              ) as OfferPlusType
            )?.offerPlusEn ?? 0;
          const offerPlusFr =
            (
              offer.offertypes.find(
                (offerToCheck) => offerToCheck?.name === 'LeaseOffersPlus',
              ) as OfferPlusType
            )?.offerPlusFr ?? 0;
          return {
            ...baseVehicleInfo,
            aprRate,
            creditBonus,
            customerDownPayment,
            monthlyPayment,
            offerType: 'lease',
            programName: 'AUD_APR_SUBVENTION_SPEND',
            term,
            totalDueAtSign: customerDownPayment,
            offerPlusEn,
            offerPlusFr,
            paymentFreq,
          };
        }
        return {
          ...baseVehicleInfo,
          aprRate: 0,
          creditBonus: 0,
          customerDownPayment: 0,
          monthlyPayment: 0,
          offerType: 'lease',
          programName: 'AUD_APR_SUBVENTION_SPEND',
          term: 0,
          totalDueAtSign: 0,
        };
      });
    });

  const cpoOffers = rawCaOffers
    ?.filter((toCheck) => toCheck.type === 'USED-CAR')
    .map((vehicle) => {
      const featuredOfferImageUrl = vehicle.image;
      const offerDisclaimer = vehicle.legalCopyEn ?? '';
      const offerDisclaimerFr = vehicle.legalCopyFr ?? '';
      const offerImageUrl = vehicle.image;
      const offerModelDetail = {
        bodyType: adjustBodyType(vehicle.bodyStyleEn),
        drivetrain: null,
        fuelType: null,
        modelCode: vehicle.modelSalesCode,
        modelCodeDescription: vehicle.mtlTrimName ?? '',
        modelFamily: replaceModelFamily(vehicle.modelFamily, vehicle.modelName),
        modelYear: vehicle.year,
        modelName: adjustModelName(vehicle.modelName, vehicle.year, lang),
        msrp: vehicle.msrp ?? 0,
        productModelCode: vehicle.modelSalesCode,
        trimline: 'base',
        programPriority: vehicle.featuredOffer ? 1 : 2,
        programShortDescription: offerDisclaimer,
        programToDate: dateFormatter(vehicle.expiryDate, lang),
        dag: vehicle.dag,
        vehicleType: vehicle.type,
        buildAudiUrlEn: '',
        buildAudiUrlFr: '',
        modelId: vehicle.modelId,
      };

      const baseVehicleInfo = {
        offerDisclaimer,
        featuredOfferImageUrl,
        offerDisclaimerFr,
        offerImageUrl,
        offerModelDetail,
        stackableOffers: [],
      };
      return {
        ...baseVehicleInfo,
        aprRate: 0,
        creditBonus: 0,
        customerDownPayment: 0,
        monthlyPayment: 0,
        offerType: 'finance',
        programName: 'AUD_APR_SUBVENTION_SPEND',
        term: 0,
        totalDueAtSign: 0,
        zipClusterID: 'null',
        programPriority: vehicle.featuredOffer ? 1 : 2,
        programToDate: dateFormatter(vehicle.expiryDate, lang),
        programFromDate: 'null',
        programShortDescription: 'null',
        region: 'null',
        calendarMonth: 'null',
        offerPlusEn: 0,
        offerPlusFr: 0,
        cpoOffers: combineCpoOffers(vehicle.cpoOffers),
      };
    });
  const caOffers = useMemo(() => {
    if (newOffers && cpoOffers) return [...newOffers, ...cpoOffers];
    return [];
  }, [cpoOffers, newOffers]);

  return { caOffers, loadingCaOffers: loading };
};

export default useFetchCanOffers;
